/* eslint-disable no-console */
import { useEffect, useRef, useCallback } from 'preact/hooks'
import { CarouselServiceProps } from './types'
import { carouselWindowListenerManager } from './carousel-window-listener-manager'
import { resizeManager } from './resize-manager'
import useDetectBreakpoint from '../hooks/useDetectBreakpoint'

/*
 TODO:
 - Use requestAnimationFrame or idle frames to do resizing.
 - update README.md
*/

export default function CarouselService({
  children,
  className = '',
  classWrap = '',
  carouselClasses = '',
  selector = 'js-carousel-service',
  scrollbar = true,
  isVertical = false,
  style = {},
  isMobile = false,
  currentBreakPointOption = {},
  numberOfChildren = 0,
  addJustifyCenter = true
}: CarouselServiceProps) {
  const { isMobile: isMobileViewport } = useDetectBreakpoint()
  const scrollSpeed = isMobileViewport ? 0.6 : 1

  // Refs for DOM elements
  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const scrubRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)

  // Refs for state tracking
  const isOverScroller = useRef(false)
  const isLeftMouseDown = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const scrollLeft = useRef(0)
  const scrollTop = useRef(0)
  const scrubState = useRef({
    dragging: false,
    pointerId: null as number | null
  })

  // Track drag state to suppress click navigation after dragging
  const didDrag = useRef(false)
  const DRAG_THRESHOLD = 5

  const measure = () => {
    if (!scrollerRef.current || !trackRef.current || !thumbRef.current) return

    const scroller = scrollerRef.current
    const track = trackRef.current
    const thumb = thumbRef.current

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    const trackW = track.getBoundingClientRect().width
    const thumbW = maxScroll === 0 ? trackW : trackW * (scroller.clientWidth / scroller.scrollWidth)

    thumb.style.width = `${Math.max(24, thumbW)}px`
    updateThumb(maxScroll, trackW, thumbW)
  }

  // Update thumb position
  const updateThumb = (maxScroll: number, trackW: number, thumbW: number) => {
    if (!scrollerRef.current || !scrubRef.current || !thumbRef.current) return

    const scroller = scrollerRef.current
    const scrub = scrubRef.current
    const thumb = thumbRef.current

    const progress = maxScroll === 0 ? 0 : scroller.scrollLeft / maxScroll
    const maxLeft = Math.max(0, trackW - thumbW)

    thumb.style.left = `${progress * maxLeft}px`
    scrub.setAttribute('aria-valuenow', Math.round(progress * 100).toString())

    // Show/hide scrub based on overflow
    const slidesPerView = Number(currentBreakPointOption?.slidesPerView)
    const carouselContainerHasOverflow = slidesPerView && numberOfChildren > slidesPerView
    const hasOverflow = maxScroll > 0 || carouselContainerHasOverflow

    scrub.style.display = hasOverflow ? 'block' : 'none'
    scroller.style.justifyContent = hasOverflow ? '' : 'center'

    // need to add an override as 1 carousel does not want to center
    if (addJustifyCenter === false) {
      scroller.style.justifyContent = ''
    }
  }

  // Handle scrub drag
  const setScrollFromClientX = (clientX: number) => {
    if (!trackRef.current || !scrollerRef.current) return

    const track = trackRef.current
    const scroller = scrollerRef.current
    const rect = track.getBoundingClientRect()

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    const trackW = rect.width
    const thumbW = maxScroll === 0 ? trackW : trackW * (scroller.clientWidth / scroller.scrollWidth)

    const x = clientX - rect.left - thumbW / 2
    const p = Math.min(1, Math.max(0, x / (rect.width - thumbW)))

    scroller.scrollTo({ left: p * maxScroll, behavior: 'auto' })
  }

  // Handle scroll events on the carousel and then update the scrub bar dimensions
  const handleScroll = useCallback(() => {
    requestAnimationFrame(measure)
  }, [])

  // Re-measure when currentBreakPointOption changes (affects layout)
  useEffect(() => {
    if (!scrollerRef.current) return

    // Delay to ensure DOM has updated with new dimensions
    const timeoutId = setTimeout(() => {
      measure()
    }, 150)

    return () => clearTimeout(timeoutId)
  }, [currentBreakPointOption, numberOfChildren])

  // Use ResizeObserver to detect when scroller content size changes
  useEffect(() => {
    if (!scrollerRef.current) return

    const scroller = scrollerRef.current
    const resizeObserver = new ResizeObserver(() => {
      measure()
    })

    resizeObserver.observe(scroller)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!carouselRef.current || !scrollerRef.current) return

    const scroller = scrollerRef.current
    const scrub = scrubRef.current

    const handleMouseEnter = () => {
      isOverScroller.current = true
    }

    const handleMouseLeave = () => {
      isOverScroller.current = false
      scroller?.classList?.remove('dragging')
    }

    const handlePointerDownGlobal = (e: PointerEvent) => {
      if (e.button === 0) isLeftMouseDown.current = true
    }

    const handlePointerUpGlobal = (e: PointerEvent) => {
      if (e.button === 0) isLeftMouseDown.current = false
    }

    // Vertical carousel handlers
    if (isVertical) {
      const handlePointerDown = (e: PointerEvent) => {
        if ((e.pointerType === 'mouse' && e.button !== 0) || !scroller) return
        didDrag.current = false
        startY.current = e.pageY - scroller.offsetTop
        scrollTop.current = scroller.scrollTop
        scroller?.classList?.add('dragging')
      }

      const handleMouseMove = (e: MouseEvent) => {
        if (!isOverScroller.current || !scrollerRef.current || !isLeftMouseDown.current) {
          scroller?.classList?.remove('dragging')

          return
        }

        const y = e.pageY - scrollerRef.current.offsetTop
        const walk = (y - startY.current) * scrollSpeed

        if (Math.abs(y - startY.current) > DRAG_THRESHOLD) {
          didDrag.current = true
        }

        scrollerRef.current.scrollTop = scrollTop.current - walk
      }

      const handleClickCapture = (e: MouseEvent) => {
        if (!didDrag.current) return

        const target = e.target as HTMLElement

        if (target.closest('a, button')) {
          e.preventDefault()
          e.stopPropagation()
          ;(e as any).stopImmediatePropagation?.()
        }
        didDrag.current = false
      }

      scroller.addEventListener('pointerdown', handlePointerDown)
      scroller.addEventListener('mouseenter', handleMouseEnter)
      scroller.addEventListener('mouseleave', handleMouseLeave)
      scroller.addEventListener('click', handleClickCapture, true)
      carouselWindowListenerManager.register('mousemove', handleMouseMove)
      carouselWindowListenerManager.register('pointerdown', handlePointerDownGlobal)
      carouselWindowListenerManager.register('pointerup', handlePointerUpGlobal)
      carouselWindowListenerManager.register('pointercancel', handlePointerUpGlobal)

      scroller.style.cursor = 'grab'

      return () => {
        scroller.removeEventListener('pointerdown', handlePointerDown)
        scroller.removeEventListener('mouseenter', handleMouseEnter)
        scroller.removeEventListener('mouseleave', handleMouseLeave)
        scroller.removeEventListener('click', handleClickCapture, true)
        scroller.style.cursor = ''
        carouselWindowListenerManager.unregister('mousemove', handleMouseMove)
        carouselWindowListenerManager.unregister('pointerdown', handlePointerDownGlobal)
        carouselWindowListenerManager.unregister('pointerup', handlePointerUpGlobal)
        carouselWindowListenerManager.unregister('pointercancel', handlePointerUpGlobal)
      }
    }

    // Horizontal carousel handlers
    const handleCarouselPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (!scrollerRef.current) return
      didDrag.current = false
      startX.current = e.pageX - scrollerRef.current.offsetLeft
      scrollLeft.current = scrollerRef.current.scrollLeft
      scrollerRef.current?.classList.add('dragging')
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isOverScroller.current || !scrollerRef.current || !isLeftMouseDown.current) {
        scroller?.classList?.remove('dragging')

        return
      }

      const x = e.pageX - scrollerRef.current.offsetLeft
      const walk = (x - startX.current) * scrollSpeed

      if (Math.abs(x - startX.current) > DRAG_THRESHOLD) {
        didDrag.current = true
      }

      scrollerRef.current.scrollLeft = scrollLeft.current - walk
    }

    const handleClickCapture = (e: MouseEvent) => {
      if (!didDrag.current) return

      const target = e.target as HTMLElement

      if (target.closest('a, button')) {
        e.preventDefault()
        e.stopPropagation()
        ;(e as any).stopImmediatePropagation?.()
      }
      didDrag.current = false
    }

    const handleScrubPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return

      scrubState.current.dragging = true
      scrubState.current.pointerId = e.pointerId
      scrub?.classList.add('dragging')
      scrub?.setPointerCapture(e.pointerId)
      setScrollFromClientX(e.clientX)
    }

    const handleScrubPointerMove = (e: PointerEvent) => {
      if (!scrubState.current.dragging) return

      setScrollFromClientX(e.clientX)
    }

    const handleScrubPointerUp = () => {
      const pointerId = scrubState.current.pointerId

      scrubState.current.dragging = false
      scrubState.current.pointerId = null
      scrub?.classList.remove('dragging')

      if (pointerId !== null) {
        scrub?.releasePointerCapture?.(pointerId)
      }
    }

    const handleResize = () => {
      measure()
    }

    // Add horizontal carousel event listeners
    scroller.addEventListener('scroll', handleScroll, { passive: true })
    scroller.addEventListener('pointerdown', handleCarouselPointerDown)
    scroller.addEventListener('mouseenter', handleMouseEnter)
    scroller.addEventListener('mouseleave', handleMouseLeave)
    scroller.addEventListener('click', handleClickCapture, true)
    carouselWindowListenerManager.register('mousemove', handleMouseMove)
    carouselWindowListenerManager.register('pointerdown', handlePointerDownGlobal)
    carouselWindowListenerManager.register('pointerup', handlePointerUpGlobal)
    carouselWindowListenerManager.register('pointercancel', handlePointerUpGlobal)
    resizeManager.register(handleResize)

    if (scrub) {
      scrub.addEventListener('pointerdown', handleScrubPointerDown)
      scrub.addEventListener('pointermove', handleScrubPointerMove)
      scrub.addEventListener('pointerup', handleScrubPointerUp)
      scrub.addEventListener('pointercancel', handleScrubPointerUp)
      scrub.addEventListener('lostpointercapture', handleScrubPointerUp)
    }

    // Measure and initialize
    measure()

    // Cleanup
    return () => {
      scroller.removeEventListener('scroll', handleScroll)
      scroller.removeEventListener('pointerdown', handleCarouselPointerDown)
      scroller.removeEventListener('mouseenter', handleMouseEnter)
      scroller.removeEventListener('mouseleave', handleMouseLeave)
      scroller.removeEventListener('click', handleClickCapture, true)
      carouselWindowListenerManager.unregister('mousemove', handleMouseMove)
      carouselWindowListenerManager.unregister('pointerdown', handlePointerDownGlobal)
      carouselWindowListenerManager.unregister('pointerup', handlePointerUpGlobal)
      carouselWindowListenerManager.unregister('pointercancel', handlePointerUpGlobal)
      resizeManager.unregister(handleResize)

      if (scrub) {
        scrub.removeEventListener('pointerdown', handleScrubPointerDown)
        scrub.removeEventListener('pointermove', handleScrubPointerMove)
        scrub.removeEventListener('pointerup', handleScrubPointerUp)
        scrub.removeEventListener('pointercancel', handleScrubPointerUp)
        scrub.removeEventListener('lostpointercapture', handleScrubPointerUp)
      }
    }
  }, [isVertical, scrollSpeed])

  return (
    <div
      ref={carouselRef}
      className={`carousel-service ${className} ${classWrap}`}
      data-selector={selector}
      style={style}
    >
      <div
        ref={scrollerRef}
        className={`carousel ${isVertical ? 'vertical' : ''} ${carouselClasses}`}
      >
        {children}
      </div>

      {scrollbar && !isVertical && (
        <div
          ref={scrubRef}
          className="scrub"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={0}
          style={{
            position: 'relative',
            margin: '1.5rem auto 0',
            height: '20px',
            cursor: 'grab',
            ...(isMobile ? { width: '100%' } : {})
          }}
        >
          <div
            ref={trackRef}
            className="scrub__track"
            style={{
              position: 'absolute',
              insetInline: 0,
              top: '50%',
              height: '1px',
              backgroundColor: 'rgba(45, 44, 44, 1)'
            }}
          />
          <div
            ref={thumbRef}
            className="scrub__thumb"
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              height: '0.20rem',
              transform: 'translateY(-100%)',
              backgroundColor: 'rgba(18, 18, 18, 1)',
              pointerEvents: 'none'
            }}
          />
        </div>
      )}
    </div>
  )
}

// Carousel slide component
export function CarouselSlide({
  children,
  className = '',
  style = {}
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div className={`carousel-slide ${className}`} style={{ flexShrink: 0, ...style }}>
      {children}
    </div>
  )
}
